import { FORMAT_VERSION, IDENTITY_KEYS, PROFILE_KINDS } from './profile-document';

const ROW_KEYS = {
  [PROFILE_KINDS.EXPORT]: ['mappings'],
  [PROFILE_KINDS.IMPORT]: ['mappings', 'defaults'],
  [PROFILE_KINDS.DATA_TABLE]: ['columns'],
};

const describeIdentity = (value) => (value === undefined ? 'nothing' : JSON.stringify(value));

const PRESENCE_ONLY_KEYS = ['importName'];

// Compares the file's identity against the profile on screen.
// import type with no importName must not be promoted onto one that has a name, and vice versa.
const identityErrors = (kind, fileIdentity, targetIdentity) =>
  IDENTITY_KEYS[kind].reduce((errors, key) => {
    const fromFile = fileIdentity?.[key] ?? undefined;
    const onScreen = targetIdentity?.[key] ?? undefined;
    const describe = `This file is for ${key} ${describeIdentity(fromFile)}, but ${describeIdentity(onScreen)} is selected. `;

    if (PRESENCE_ONLY_KEYS.includes(key)) {
      if ((fromFile === undefined) === (onScreen === undefined)) return errors;
      return errors.concat(
        `${describe}Select a profile that uses an import name; the names themselves do not need to match.`,
      );
    }

    if (fromFile === onScreen) return errors;
    return errors.concat(`${describe}Select the matching profile, or download the profile you meant to upload.`);
  }, []);

export const parseProfileDocument = (text, { kind, identity }) => {
  let document;
  try {
    document = JSON.parse(text);
  } catch {
    return { errors: ['This file is not valid JSON.'] };
  }

  if (document?.formatVersion !== FORMAT_VERSION) {
    return {
      errors: [
        document?.formatVersion === undefined
          ? 'This file was exported by an older version of the admin panel. Please download the profile again and re-upload it.'
          : `This file uses profile format version ${document.formatVersion}, but this admin panel reads version ${FORMAT_VERSION}. Please download the profile again.`,
      ],
    };
  }

  if (document.kind !== kind) {
    return {
      errors: [`This file holds a ${describeIdentity(document.kind)}, but a ${describeIdentity(kind)} is selected.`],
    };
  }

  const errors = identityErrors(kind, document.identity, identity);

  if (!document.profile || typeof document.profile !== 'object' || Array.isArray(document.profile)) {
    errors.push('This file has no profile section.');
  }
  ROW_KEYS[kind].forEach((rowKey) => {
    if (!Array.isArray(document[rowKey])) errors.push(`This file has no ${rowKey} list.`);
  });

  return errors.length ? { errors } : { document };
};
